import { CaregiverApiDto, toCaregiver } from "@/model/Caregiver";
import { ClinicalNote, CreateClinicalNote, toCreateClinicalNoteApiDto } from "@/model/ClinicalNote";
import {
  PersonalizedDiabetesProtocol,
  PersonalizedHypertensionProtocol,
  toPersonalizedDiabetesProtocolApiDto,
  toPersonalizedHypertensionProtocolApiDto,
} from "@/model/ClinicalProtocol";
import { MedicationApiDto } from "@/model/Medication";
import { toOrganization } from "@/model/Organization";
import {
  OffboardingRequest,
  toDiabetesPatient,
  toHypertensionPatient,
  UnreviewedDiabetesReadings,
  UnreviewedHypertensionReadings,
} from "@/model/Patient";
import {
  DiabetesFieldsApiDto,
  hasDiabetes,
  hasHypertension,
  HypertensionFieldsApiDto,
  PatientApiDto,
} from "@/model/PatientApiDto";
import { updateIfDefined } from "@/util/object-utils";
import { QuerySelectOption } from "@/util/query-utils";
import { ensure } from "@/util/type-utils";
import { Instant, LocalDate } from "@js-joda/core";
import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addClinicalNote,
  checkResetPasswordToken,
  CheckResetPasswordTokenParams,
  checkSignUpInvitation,
  CheckSignUpInvitationParams,
  getBloodGlucoseObservations,
  getBloodPressureObservations,
  getClinicalNotes,
  GetObservationsOptions,
  getOrganization,
  getPatient,
  getPatientsForOrganization,
  getPrescribingCaregivers,
  getSelfUser,
  logInWithEmailAndPassword,
  logInWithMfaCode,
  logOut,
  offboardPatient,
  resendInvitationEmail,
  resetPassword,
  sendForgotPasswordEmail,
  setPatientEducation,
  setStickyNote,
  signUp,
  updateDeliveryDate,
  updateDiabetesMedications,
  updatePersonalizedDiabetesProtocol,
  updatePersonalizedHypertensionProtocol,
} from "./requests";

export const useLogInMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: logInWithEmailAndPassword,
    onSuccess: (result) => {
      if (result.type === "authenticated") queryClient.invalidateQueries();
    },
    onError: console.error,
  });
};

export const useLogInWithMfaCodeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: logInWithMfaCode,
    onSuccess: () => queryClient.invalidateQueries(),
    onError: console.error,
  });
};

export const useLogOutMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => logOut(),
    onMutate: () => queryClient.cancelQueries(),
    onSuccess: () => {
      queryClient.clear();
      return undefined;
    },
    onError: console.error,
  });
};

export const useSelfUserQuery = () =>
  useQuery({
    queryKey: ["self-user"],
    queryFn: async () => {
      const user = await getSelfUser();
      return toCaregiver(user);
    },
  });

const ensureSelfUser = (queryClient: QueryClient) =>
  queryClient.query<CaregiverApiDto>({ queryKey: ["self-user"], staleTime: "static" });

export const useCheckSignUpInvitationQuery = (params: CheckSignUpInvitationParams) =>
  useQuery({
    queryKey: ["check-invitation", params],
    queryFn: async () => {
      await checkSignUpInvitation(params);
      return true; // react-query demands we return something to put in the cache
    },
    retry: 0,
    staleTime: 0,
    gcTime: 0,
  });

export const useSignUpMutation = () => {
  return useMutation({
    mutationFn: signUp,
    onError: console.error,
  });
};

export const useResendInvitationEmailMutation = () => {
  return useMutation({
    mutationFn: resendInvitationEmail,
    onError: console.error,
  });
};

export const useSendForgotPasswordEmailMutation = () => {
  return useMutation({
    mutationFn: sendForgotPasswordEmail,
    onError: console.error,
  });
};

export const useCheckResetPasswordTokenQuery = (params: CheckResetPasswordTokenParams) =>
  useQuery({
    queryKey: ["check-reset-password-token", params],
    queryFn: async () => {
      await checkResetPasswordToken(params);
      return true; // react-query demands we return something to put in the cache
    },
    retry: 0,
    staleTime: 0,
    gcTime: 0,
  });

export const useResetPasswordMutation = () => {
  return useMutation({
    mutationFn: resetPassword,
    retry: 0,
    onError: console.error,
  });
};

export const useOrganizationQuery = () => {
  return useQuery({
    queryKey: ["organization"],
    queryFn: async ({ client }) => {
      const { organizationId } = await ensureSelfUser(client);
      return await getOrganization(organizationId);
    },
    select: toOrganization,
    refetchOnWindowFocus: true,
  });
};

type PatientsQueryOptions<TOutput> = QuerySelectOption<PatientApiDto[], TOutput[]>;

const usePatientsQuery = <T>({ select }: PatientsQueryOptions<T>) => {
  return useQuery({
    queryKey: ["patients"],
    queryFn: async ({ client }) => {
      const { organizationId } = await ensureSelfUser(client);
      return await getPatientsForOrganization(organizationId);
    },
    refetchInterval: 30_000, // 30 seconds
    gcTime: 3_600_000, // 1 hour
    select,
  });
};

export const useHypertensionPatientsQuery = () =>
  usePatientsQuery({
    select: (patientDtos) => patientDtos.filter(hasHypertension).map(toHypertensionPatient),
  });

export const useDiabetesPatientsQuery = () =>
  usePatientsQuery({
    select: (patientDtos) => patientDtos.filter(hasDiabetes).map(toDiabetesPatient),
  });

export const useHypertensionPatientQuery = (patientId: string) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["patients", patientId],
    initialData: () => {
      const patients = queryClient.getQueryData<PatientApiDto[]>(["patients"]);
      const patient = patients?.find((patient) => patient.id === patientId);
      if (patient) {
        ensure(
          patient,
          hasHypertension,
          (patient) => `Patient ${patient.id} is not a hypertension patient`
        );
        return { ...patient, unreviewedReadingsUpToDate: false };
      } else {
        return undefined;
      }
    },
    queryFn: async ({ client }) => {
      const { organizationId } = await ensureSelfUser(client);
      const patientDto = await getPatient({ patientId, organizationId });
      ensure(
        patientDto,
        hasHypertension,
        (patient) => `Patient ${patient.id} is not a hypertension patient`
      );
      return { ...patientDto, unreviewedReadingsUpToDate: true };
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
    select: (patient) => toHypertensionPatient(patient),
  });
};

export const useDiabetesPatientQuery = (patientId: string) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["patients", patientId],
    initialData: () => {
      const patients = queryClient.getQueryData<PatientApiDto[]>(["patients"]);
      const patient = patients?.find((patient) => patient.id === patientId);
      if (patient) {
        ensure(
          patient,
          hasDiabetes,
          (patient) => `Patient ${patient.id} is not a diabetes patient`
        );
        return { ...patient, unreviewedReadingsUpToDate: false };
      } else {
        return undefined;
      }
    },
    queryFn: async ({ client }) => {
      const { organizationId } = await ensureSelfUser(client);
      const patientDto = await getPatient({ patientId, organizationId });
      ensure(
        patientDto,
        hasDiabetes,
        (patient) => `Patient ${patient.id} is not a diabetes patient`
      );
      return { ...patientDto, unreviewedReadingsUpToDate: true };
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
    select: (patient) => toDiabetesPatient(patient),
  });
};

export const useSetPatientEducationMutation = () => {
  const queryClient = useQueryClient();
  const patientsQueryKey = ["patients"];
  return useMutation({
    mutationFn: setPatientEducation,
    onMutate: async ({ prescriptionId, educationProvided }) => {
      await queryClient.cancelQueries({ queryKey: patientsQueryKey });
      const currentPatients = queryClient.getQueryData<PatientApiDto[]>(patientsQueryKey) ?? [];
      const now = Instant.now().toString();
      const updatedPatients = currentPatients.map((patient) => ({
        ...patient,
        hypertension:
          patient.hypertension?.prescriptionId === prescriptionId
            ? { ...patient.hypertension, educationProvidedAt: educationProvided ? now : undefined }
            : patient.hypertension,
        diabetes:
          patient.diabetes?.prescriptionId === prescriptionId
            ? { ...patient.diabetes, educationProvidedAt: educationProvided ? now : undefined }
            : patient.diabetes,
      }));
      queryClient.setQueryData<PatientApiDto[]>(patientsQueryKey, updatedPatients);
      return () => {
        queryClient.setQueryData<PatientApiDto[]>(patientsQueryKey, currentPatients);
      };
    },
    onError: (err, _value, rollback) => {
      console.error(err);
      if (rollback) {
        rollback();
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: patientsQueryKey });
    },
  });
};

export const useStickyNoteMutation = () => {
  const queryClient = useQueryClient();
  const patientsQueryKey = ["patients"];
  return useMutation({
    mutationFn: setStickyNote,
    onMutate: async ({ prescriptionId, stickyNote }) => {
      await queryClient.cancelQueries({ queryKey: patientsQueryKey });
      const currentPatients = queryClient.getQueryData<PatientApiDto[]>(patientsQueryKey) ?? [];
      const updatedPatients = currentPatients.map((patient) => ({
        ...patient,
        hypertension:
          patient.hypertension?.prescriptionId === prescriptionId
            ? { ...patient.hypertension, stickyNote }
            : patient.hypertension,
        diabetes:
          patient.diabetes?.prescriptionId === prescriptionId
            ? { ...patient.diabetes, stickyNote }
            : patient.diabetes,
      }));
      queryClient.setQueryData<PatientApiDto[]>(patientsQueryKey, updatedPatients);
      return () => {
        queryClient.setQueryData<PatientApiDto[]>(patientsQueryKey, currentPatients);
      };
    },
    onError: (err, _value, rollback) => {
      console.error(err);
      if (rollback) {
        rollback();
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: patientsQueryKey });
    },
  });
};

export const usePrescribingCaregiversQuery = () => {
  return useQuery({
    queryKey: ["caregivers"],
    queryFn: async ({ client }) => {
      const { organizationId } = await ensureSelfUser(client);
      return await getPrescribingCaregivers({ organizationId });
    },
    refetchOnWindowFocus: true,
  });
};

export const useBloodPressureObservationsQuery = ({
  patientId,
  from,
  to,
}: Pick<GetObservationsOptions, "patientId" | "from" | "to">) => {
  return useQuery({
    queryKey: ["blood-pressure-observations", patientId, from.toString(), to.toString()],
    queryFn: async ({ client }) => {
      const { organizationId } = await ensureSelfUser(client);
      return await getBloodPressureObservations({ patientId, organizationId, from, to });
    },
  });
};

export const useBloodGlucoseObservationsQuery = ({
  patientId,
  from,
  to,
}: Pick<GetObservationsOptions, "patientId" | "from" | "to">) => {
  return useQuery({
    queryKey: ["blood-glucose-observations", patientId, from.toString(), to.toString()],
    queryFn: async ({ client }) => {
      const { organizationId } = await ensureSelfUser(client);
      return await getBloodGlucoseObservations({ patientId, organizationId, from, to });
    },
  });
};

type ClinicalNotesOptions = {
  patientId: string;
} & QuerySelectOption<ClinicalNote[]>;

const useClinicalNotesQuery = ({ patientId, ...otherOptions }: ClinicalNotesOptions) => {
  return useQuery({
    queryKey: ["clinical-notes", patientId],
    queryFn: async ({ client }) => {
      const { organizationId } = await ensureSelfUser(client);
      return await getClinicalNotes({ patientId, organizationId });
    },
    ...otherOptions,
  });
};

export const useHypertensionClinicalNotesQuery = (patientId: string) =>
  useClinicalNotesQuery({
    patientId,
    select: (clinicalNotes) => clinicalNotes.filter(({ type }) => type === "hypertension"),
  });

export const useDiabetesClinicalNotesQuery = (patientId: string) =>
  useClinicalNotesQuery({
    patientId,
    select: (clinicalNotes) => clinicalNotes.filter(({ type }) => type === "diabetes"),
  });

export const useAddClinicalNoteMutation = ({ patientId }: { patientId: string }) => {
  const queryClient = useQueryClient();
  const patientsQueryKey = ["patients"];
  const patientQueryKey = ["patients", patientId];
  const clinicalNotesQueryKey = ["clinical-notes", patientId];

  return useMutation({
    mutationFn: async (clinicalNote: CreateClinicalNote) => {
      const { organizationId } = await ensureSelfUser(queryClient);
      await addClinicalNote({
        patientId,
        organizationId,
        note: toCreateClinicalNoteApiDto(clinicalNote),
      });
    },
    onMutate: async (clinicalNote) => {
      await queryClient.cancelQueries({ queryKey: patientsQueryKey });
      await queryClient.cancelQueries({ queryKey: patientQueryKey });
      const currentPatients = queryClient.getQueryData<PatientApiDto[]>(patientsQueryKey) ?? [];
      const currentPatient = queryClient.getQueryData<PatientApiDto>(patientQueryKey);
      const updatedPatients = currentPatients.map((patient) =>
        patient.id === patientId ? updatePatientFromClinicalNote(patient, clinicalNote) : patient
      );
      const updatedCurrentPatient = currentPatient
        ? updatePatientFromClinicalNote(currentPatient, clinicalNote)
        : undefined;
      queryClient.setQueryData<PatientApiDto[]>(patientsQueryKey, updatedPatients);
      queryClient.setQueryData<PatientApiDto>(patientQueryKey, updatedCurrentPatient);
      return () => {
        queryClient.setQueryData<PatientApiDto[]>(patientsQueryKey, currentPatients);
        queryClient.setQueryData<PatientApiDto>(patientQueryKey, currentPatient);
      };
    },
    onSuccess: async (_data, clinicalNote) => {
      await queryClient.cancelQueries({ queryKey: clinicalNotesQueryKey });

      let user: CaregiverApiDto | null = null;
      try {
        user = await ensureSelfUser(queryClient);
      } catch (error) {
        console.error(error);
      }

      const currentClinicalNotes =
        queryClient.getQueryData<ClinicalNote[]>(clinicalNotesQueryKey) ?? [];

      const now = Instant.now();
      const randomishPlaceholderId = `${patientId}-${now.epochSecond()}`;
      const updatedClinicalNotes = currentClinicalNotes.concat([
        {
          ...clinicalNote,
          id: randomishPlaceholderId,
          patientId,
          creatorName: user ? (user.displayName ?? `${user.firstName} ${user.lastName}`) : "",
          dateTime: now,
        },
      ]);
      queryClient.setQueryData<ClinicalNote[]>(clinicalNotesQueryKey, updatedClinicalNotes);
    },
    onError: (err, _value, rollback) => {
      console.error(err);
      if (rollback) {
        rollback();
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: patientsQueryKey });
      queryClient.invalidateQueries({ queryKey: clinicalNotesQueryKey });
    },
  });
};

const updatePatientFromClinicalNote = (
  patient: PatientApiDto,
  clinicalNote: CreateClinicalNote
): PatientApiDto => ({
  ...patient,
  lastLiveInteractionTime: clinicalNote.livePatientInteraction
    ? Instant.now().toString()
    : patient.lastLiveInteractionTime,
  clinicalTimeForCurrentMonthInSeconds:
    patient.clinicalTimeForCurrentMonthInSeconds + clinicalNote.timeSpent.seconds(),
  hypertension:
    clinicalNote.type === "hypertension"
      ? {
          ...patient.hypertension!,
          unreviewedReadings:
            clinicalNote.actionType === "review"
              ? zeroUnreviewedHypertensionReadings()
              : patient.hypertension!.unreviewedReadings,
        }
      : patient.hypertension,
  diabetes:
    clinicalNote.type === "diabetes"
      ? {
          ...patient.diabetes!,
          unreviewedReadings:
            clinicalNote.actionType === "review"
              ? zeroUnreviewedDiabetesReadings()
              : patient.diabetes!.unreviewedReadings,
        }
      : patient.diabetes,
});

export const useUpdatePersonalizedHypertensionProtocolMutation = ({
  patientId,
}: {
  patientId: string;
}) => {
  const queryClient = useQueryClient();
  const patientQueryKey = ["patients", patientId];
  return useMutation({
    mutationFn: async ({
      prescriptionId,
      personalizedClinicalProtocol,
    }: {
      prescriptionId: string;
      personalizedClinicalProtocol: PersonalizedHypertensionProtocol;
    }) => {
      await updatePersonalizedHypertensionProtocol({
        prescriptionId,
        personalizedProtocol: toPersonalizedHypertensionProtocolApiDto(
          personalizedClinicalProtocol
        ),
      });
    },
    onSuccess: async (_, { personalizedClinicalProtocol }) => {
      await queryClient.cancelQueries({ queryKey: patientQueryKey });
      const currentPatient = queryClient.getQueryData<PatientApiDto>(patientQueryKey)!;
      const updatedPatient: PatientApiDto = {
        ...currentPatient,
        hypertension: {
          ...currentPatient.hypertension!,
          personalizedClinicalProtocol: toPersonalizedHypertensionProtocolApiDto(
            personalizedClinicalProtocol
          ),
        },
      };
      queryClient.setQueryData<PatientApiDto>(patientQueryKey, updatedPatient);
    },
    onError: console.error,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["patients"] });
    },
  });
};

export const useUpdatePersonalizedDiabetesProtocolMutation = ({
  patientId,
}: {
  patientId: string;
}) => {
  const queryClient = useQueryClient();
  const patientQueryKey = ["patients", patientId];
  return useMutation({
    mutationFn: async ({
      prescriptionId,
      personalizedClinicalProtocol,
    }: {
      prescriptionId: string;
      personalizedClinicalProtocol: PersonalizedDiabetesProtocol;
    }) => {
      await updatePersonalizedDiabetesProtocol({
        prescriptionId,
        personalizedProtocol: toPersonalizedDiabetesProtocolApiDto(personalizedClinicalProtocol),
      });
    },
    onSuccess: async (_, { personalizedClinicalProtocol }) => {
      await queryClient.cancelQueries({ queryKey: patientQueryKey });
      const currentPatient = queryClient.getQueryData<PatientApiDto>(patientQueryKey)!;
      const updatedPatient: PatientApiDto = {
        ...currentPatient,
        diabetes: {
          ...currentPatient.diabetes!,
          personalizedClinicalProtocol: toPersonalizedDiabetesProtocolApiDto(
            personalizedClinicalProtocol
          ),
        },
      };
      queryClient.setQueryData<PatientApiDto>(patientQueryKey, updatedPatient);
    },
    onError: console.error,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["patients"] });
    },
  });
};

export const useUpdateDiabetesMedicationsMutation = ({ patientId }: { patientId: string }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (medications: MedicationApiDto[]) => {
      const { organizationId } = await ensureSelfUser(queryClient);
      await updateDiabetesMedications({ patientId, organizationId, medications });
      await queryClient.invalidateQueries({ queryKey: ["patients"] });
    },
    onError: console.error,
  });
};

const zeroUnreviewedHypertensionReadings = (): UnreviewedHypertensionReadings => ({
  total: 0,
  low: 0,
  elevated: 0,
  acceptable: 0,
  critical: 0,
});

const zeroUnreviewedDiabetesReadings = (): UnreviewedDiabetesReadings => ({
  total: 0,
  emergencyLow: 0,
  criticalLow: 0,
  low: 0,
  elevated: 0,
  acceptable: 0,
  criticalHigh: 0,
  pending: 0,
  unclassified: 0,
});

export const useOffboardPatientMutation = ({ patientId }: { patientId: string }) => {
  const queryClient = useQueryClient();
  const patientsQueryKey = ["patients"];
  const patientQueryKey = ["patients", patientId];
  return useMutation({
    mutationFn: async ({ prescriptionId, reason, deliveryDate, note }: OffboardingRequest) =>
      await offboardPatient({
        prescriptionId,
        reason,
        deliveryDate: deliveryDate?.toString(),
        note,
      }),
    onMutate: async ({ deliveryDate }) => {
      await queryClient.cancelQueries({ queryKey: patientQueryKey });
      await queryClient.cancelQueries({ queryKey: patientsQueryKey });
      const currentPatient = queryClient.getQueryData<PatientApiDto>(patientQueryKey);
      const currentPatients = queryClient.getQueryData<PatientApiDto[]>(patientsQueryKey);
      if (!currentPatient) {
        return;
      }
      const update = {
        deliveryDate: deliveryDate?.toString(),
        status: "offboarded" as const,
      };
      const updatedPatient = {
        ...currentPatient,
        hypertension: updateIfDefined(currentPatient.hypertension, update),
        diabetes: updateIfDefined(currentPatient.diabetes, update),
      };
      const updatedPatients = currentPatients?.map((patient) =>
        patient.id === patientId ? updatedPatient : patient
      );
      queryClient.setQueryData<PatientApiDto>(patientQueryKey, updatedPatient);
      queryClient.setQueryData<PatientApiDto[]>(patientsQueryKey, updatedPatients);
      return () => {
        queryClient.setQueryData<PatientApiDto>(patientQueryKey, currentPatient);
        queryClient.setQueryData<PatientApiDto[]>(patientsQueryKey, currentPatients);
      };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: patientsQueryKey });
    },
    onError: (err, _value, rollback) => {
      console.error(err);
      if (rollback) {
        rollback();
      }
    },
  });
};

export const useUpdateDeliveryDateMutation = () => {
  const queryClient = useQueryClient();
  const patientsQueryKey = ["patients"];
  return useMutation({
    mutationFn: async ({
      patientId,
      deliveryDate,
    }: {
      patientId: string;
      deliveryDate: LocalDate | undefined;
    }) => {
      await updateDeliveryDate({ patientId, deliveryDate: deliveryDate?.toString() });
    },
    onMutate: async ({ patientId, deliveryDate }) => {
      const patientQueryKey = ["patients", patientId];
      await queryClient.cancelQueries({ queryKey: patientQueryKey });
      await queryClient.cancelQueries({ queryKey: patientsQueryKey });
      const currentPatient = queryClient.getQueryData<PatientApiDto>(patientQueryKey);
      const currentPatients = queryClient.getQueryData<PatientApiDto[]>(patientsQueryKey);
      if (!currentPatient) {
        return;
      }
      const { hypertension, diabetes } = currentPatient;
      const updatedPatient = {
        ...currentPatient,
        hypertension: hypertension && {
          ...hypertension,
          deliveryDate: deliveryDate?.toString(),
          status: updatedStatusFor(hypertension, deliveryDate),
        },
        diabetes: diabetes && {
          ...diabetes,
          deliveryDate: deliveryDate?.toString(),
          status: updatedStatusFor(diabetes, deliveryDate),
        },
      };
      const updatedPatients = currentPatients?.map((patient) =>
        patient.id === patientId ? updatedPatient : patient
      );
      queryClient.setQueryData<PatientApiDto>(patientQueryKey, updatedPatient);
      queryClient.setQueryData<PatientApiDto[]>(patientsQueryKey, updatedPatients);
      return () => {
        queryClient.setQueryData<PatientApiDto>(patientQueryKey, currentPatient);
        queryClient.setQueryData<PatientApiDto[]>(patientsQueryKey, currentPatients);
      };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: patientsQueryKey });
    },
    onError: (err, _value, rollback) => {
      console.error(err);
      if (rollback) {
        rollback();
      }
    },
  });
};

const updatedStatusFor = (
  prescription: HypertensionFieldsApiDto | DiabetesFieldsApiDto,
  deliveryDate: LocalDate | undefined
) => {
  const status = prescription.status;
  const isMonitoring = status === "monitoring" || status === "postpartum-monitoring";
  return !isMonitoring
    ? status
    : deliveryDate
      ? ("postpartum-monitoring" as const)
      : ("monitoring" as const);
};
